import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebglAddon } from 'xterm-addon-webgl';
import { io as socketIO } from 'socket.io-client';
import 'xterm/css/xterm.css';

const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  background: #0b0f14;
  color: #d6dee8;
  text-align: left;  /* 상위 .App 의 center 상속 차단 */
  @media(max-width:768px){height:100dvh;}
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: #11161d;
  border-bottom: 1px solid #1e2630;
  @media(max-width:768px){gap:6px;padding:8px;flex-wrap:wrap;}
`;

const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #e6edf3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: #8b96a3;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  background: ${({ status }) =>
    status === 'connected' ? '#3fb950' :
    status === 'connecting' ? '#d29922' :
    status === 'closed' ? '#8b96a3' : '#f85149'};
`;

const Btn = styled.button`
  padding: 6px 12px;
  background: ${({ danger }) => danger ? '#5c2123' : '#1f2937'};
  color: ${({ danger }) => danger ? '#ffb4b6' : '#e6edf3'};
  border: 1px solid ${({ danger }) => danger ? '#7f1d1d' : '#374151'};
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const TermBox = styled.div`
  flex: 1;
  padding: 16px 20px;
  overflow: hidden;
  background: #0b0f14;
  & .xterm,
  & .xterm-viewport,
  & .xterm-screen { height: 100% !important; }
  & .xterm .xterm-screen { padding: 0; }
`;

const InfoBar = styled.div`
  padding: 6px 16px;
  background: #11161d;
  border-top: 1px solid #1e2630;
  font-size: 11px;
  color: #8b96a3;
  display: flex;
  gap: 16px;
`;

function getToken() {
  return sessionStorage.getItem('token') || localStorage.getItem('token');
}

export default function ConsolePage() {
  const { infraId } = useParams();
  const [searchParams] = useSearchParams();
  const accountsId = searchParams.get('accounts_id');
  const navigate = useNavigate();

  const termRef = useRef(null);
  const containerRef = useRef(null);
  const fitRef = useRef(null);
  const socketRef = useRef(null);

  const [status, setStatus] = useState('connecting');  // connecting | connected | closed | error
  const [error, setError] = useState('');
  const [infraTitle, setInfraTitle] = useState('');
  const [host, setHost] = useState('');
  const [sessionId, setSessionId] = useState('');

  const cleanup = useCallback(() => {
    try { socketRef.current?.disconnect(); } catch (_) {}
    try { termRef.current?.dispose(); } catch (_) {}
    socketRef.current = null;
    termRef.current = null;
    fitRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const term = new Terminal({
      cursorBlink: true,
      fontFamily: '"Cascadia Mono", Consolas, "Courier New", Menlo, monospace',
      fontSize: 14,
      letterSpacing: 0,
      lineHeight: 1.2,
      theme: {
        background: '#0b0f14',
        foreground: '#d6dee8',
        cursor: '#3fb950'
      },
      scrollback: 5000,
      allowProposedApi: true
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);
    // WebGL 렌더러로 자간 불균일 문제 해결 (실패시 DOM 렌더러로 자동 폴백)
    try {
      const webgl = new WebglAddon();
      webgl.onContextLoss(() => webgl.dispose());
      term.loadAddon(webgl);
    } catch (e) {
      console.warn('[console] WebGL 렌더러 로드 실패, DOM 렌더러로 동작:', e?.message);
    }
    fit.fit();
    term.focus();
    termRef.current = term;
    fitRef.current = fit;

    (async () => {
      try {
        const token = getToken();
        if (!token) {
          setStatus('error');
          setError('로그인이 필요합니다.');
          return;
        }

        // accounts_id 가 URL 에 없으면 호스트의 계정 목록을 조회해 첫번째 사용
        let resolvedAccountsId = accountsId ? Number(accountsId) : null;
        if (!resolvedAccountsId) {
          const accRes = await fetch(`/api/getInfraAccountList?hostId=${infraId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const accList = await accRes.json();
          if (Array.isArray(accList) && accList.length > 0) {
            resolvedAccountsId = accList[0].accounts_id;
          } else {
            setStatus('error');
            setError('이 호스트에 등록된 SSH 계정이 없습니다. 카드의 ⚙️ 설정에서 계정을 추가하세요.');
            return;
          }
        }

        // 1) ticket 발급
        const res = await fetch('/api/console/sessions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            infra_id: Number(infraId),
            accounts_id: resolvedAccountsId
          })
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          setStatus('error');
          setError(data.error || '세션 생성 실패');
          return;
        }
        if (cancelled) return;

        setInfraTitle(data.infra?.title || '');
        setHost(data.infra?.host || '');

        // 2) socket.io 연결
        const socket = socketIO(`${window.location.origin}/console`, {
          path: '/socket.io',
          transports: ['websocket'],
          auth: { ticket: data.ticket },
          reconnection: false
        });
        socketRef.current = socket;

        socket.on('connect', () => {
          const { cols, rows } = term;
          socket.emit('start', { cols, rows });
        });

        socket.on('session', ({ sessionId: sid }) => {
          setSessionId(sid);
          setStatus('connected');
        });

        socket.on('output', (chunk) => {
          // chunk 는 Buffer/Uint8Array (binary) — socket.io 가 Uint8Array 로 줌
          if (chunk instanceof ArrayBuffer) {
            term.write(new Uint8Array(chunk));
          } else if (chunk?.type === 'Buffer' && Array.isArray(chunk.data)) {
            term.write(new Uint8Array(chunk.data));
          } else {
            term.write(chunk);
          }
        });

        socket.on('closed', ({ reason }) => {
          setStatus('closed');
          term.write(`\r\n\x1b[33m[skynet] 세션이 종료되었습니다 (${reason})\x1b[0m\r\n`);
        });

        socket.on('connect_error', (err) => {
          setStatus('error');
          setError(err?.message || '연결 오류');
          term.write(`\r\n\x1b[31m[skynet] 연결 오류: ${err?.message || err}\x1b[0m\r\n`);
        });

        socket.on('disconnect', () => {
          if (status !== 'closed') setStatus('closed');
        });

        // 3) 입력/리사이즈 핸들러
        term.onData((d) => socket.emit('input', d));
        term.onResize(({ cols, rows }) => socket.emit('resize', { cols, rows }));
      } catch (err) {
        setStatus('error');
        setError(err.message);
      }
    })();

    // 윈도우 리사이즈 → fitAddon
    const onResize = () => {
      try { fitRef.current?.fit(); } catch (_) {}
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
      cleanup();
    };
  }, [infraId, accountsId, cleanup]);

  const handleClose = () => {
    cleanup();
    navigate(-1);
  };

  return (
    <PageWrap>
      <Toolbar>
        <TitleArea>
          <Title>
            <StatusDot status={status} />
            {infraTitle || `호스트 ${infraId}`}
          </Title>
          <Subtitle>
            {host && `${host} · `}
            {status === 'connecting' && '연결 중...'}
            {status === 'connected' && '연결됨'}
            {status === 'closed' && '연결 종료'}
            {status === 'error' && `오류: ${error}`}
          </Subtitle>
        </TitleArea>
        <Btn onClick={() => fitRef.current?.fit()}>크기 맞춤</Btn>
        <Btn danger onClick={handleClose}>닫기</Btn>
      </Toolbar>
      <TermBox
        ref={containerRef}
        onClick={() => termRef.current?.focus()}
      />
      <InfoBar>
        <span>세션: {sessionId || '-'}</span>
        <span>인프라 ID: {infraId}</span>
        {accountsId && <span>계정 ID: {accountsId}</span>}
      </InfoBar>
    </PageWrap>
  );
}
